import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Users, 
  ArrowRight, 
  ShieldCheck, 
  Heart, 
  Target, 
  Compass, 
  Plus, 
  Loader2 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function WelcomePage({ user, setUser }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedFocus, setSelectedFocus] = useState('health');
  const [loading, setLoading] = useState(false);

  const focusOptions = [
    {
      id: 'health',
      title: t('welcome.focus.health.title', 'Health & Vitality'),
      desc: t('welcome.focus.health.desc', 'Build physical energy, healthy habits, and daily exercise.'),
      preview: 'Vital Person ➔ Run 5K ➔ Morning Jog',
      icon: Heart,
      color: 'text-red-500 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-950/50',
      activeBorder: 'border-red-500 ring-2 ring-red-500/20'
    },
    {
      id: 'career',
      title: t('welcome.focus.career.title', 'Career & Focus'),
      desc: t('welcome.focus.career.desc', 'Enhance productivity, achieve project goals, and plan work.'),
      preview: 'Intentional Creator ➔ Major Release ➔ Weekly Outline',
      icon: Target,
      color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-950/50',
      activeBorder: 'border-blue-500 ring-2 ring-blue-500/20'
    },
    {
      id: 'mindfulness',
      title: t('welcome.focus.mindfulness.title', 'Mindfulness & Presence'),
      desc: t('welcome.focus.mindfulness.desc', 'Cultivate daily reflection, reduce screen time, and stay present.'),
      preview: 'Grounded Explorer ➔ Sleep Better ➔ AI Reflection',
      icon: Compass,
      color: 'text-purple-500 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-950/50',
      activeBorder: 'border-purple-500 ring-2 ring-purple-500/20'
    },
    {
      id: 'blank',
      title: t('welcome.focus.blank.title', 'Explore Freely (Blank)'),
      desc: t('welcome.focus.blank.desc', 'Start with a clean sheet. Create your own custom cascade from scratch.'),
      preview: 'Empty Dashboard',
      icon: Plus,
      color: 'text-muted-foreground bg-secondary/30 border-muted/20',
      activeBorder: 'border-foreground dark:border-muted-foreground ring-2 ring-foreground/10'
    }
  ];

  const handleEnterWorkspace = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('align_token');
      const response = await axios.post(
        `${API}/users/onboard`,
        { focus: selectedFocus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update global user context so App router updates immediately
      if (setUser) {
        setUser(response.data);
      }
      
      toast.success(t('welcome.success', 'Welcome onboard! Your workspace is ready.'));
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error(t('welcome.error', 'Failed to initialize workspace. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFCF8] dark:bg-[#121212] px-4 sm:px-6 py-12 select-none animate-in fade-in duration-700">
      <div className="max-w-2xl w-full text-center space-y-8">
        
        {/* Align Logo Icon */}
        <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center animate-bounce duration-[2000ms]">
          <Sparkles className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>

        {/* Header Title */}
        <div className="space-y-3">
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#1C1B1A] dark:text-[#EAE9E4]">
            {t('welcome.trial.title', 'Welcome to Align Pro')}
          </h1>
          <p className="font-serif italic text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            {t('welcome.trial.subtitle', "Your 14-day free trial is active. Select a focus area to seed your first goal cascade.")}
          </p>
        </div>

        {/* Focus Options Selector */}
        <div className="space-y-4 text-left">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold px-1">
            {t('welcome.focus.section_title', 'Choose your primary Focus Area:')}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {focusOptions.map((opt) => {
              const Icon = opt.icon;
              const isSelected = selectedFocus === opt.id;
              return (
                <div
                  key={opt.id}
                  onClick={() => !loading && setSelectedFocus(opt.id)}
                  className={`cursor-pointer bg-card/60 hover:bg-card border rounded-2xl p-5 shadow-sm transition-all duration-200 select-none flex flex-col justify-between gap-3 ${
                    isSelected ? opt.activeBorder : 'border-border/80 hover:border-border-hover'
                  } ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center border ${opt.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground">{opt.title}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{opt.desc}</p>
                  </div>
                  
                  {/* Visual preview pill */}
                  <div className="pt-2 border-t border-border/40">
                    <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-500 bg-emerald-500/5 px-2 py-0.5 rounded border border-emerald-500/10">
                      {opt.preview}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Extra details card */}
        <div className="bg-card/30 border border-border/60 rounded-2xl p-5 text-left flex items-start gap-4 backdrop-blur-md">
          <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center flex-shrink-0 text-emerald-600 dark:text-emerald-400">
            <Users className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-semibold text-foreground">
              {t('welcome.trial.highlight', 'Pro Trial Activated')}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('welcome.trial.highlight_desc', 'You have full access to Live AI Coaching, Family/Corporate Workspaces, and Unlimited Goals. No credit card is required.')}
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
          <Button
            disabled={loading}
            onClick={handleEnterWorkspace}
            className="w-full sm:w-auto px-10 py-6 rounded-full text-sm font-medium shadow-sm transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('welcome.loading', 'Initializing Workspace...')}
              </>
            ) : (
              <>
                {t('welcome.trial.cta', 'Enter My Workspace')}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
          
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-500 flex-shrink-0" />
            <span>{t('welcome.trial.guarantee', 'No auto-charges.')}</span>
          </div>
        </div>

      </div>
    </div>
  );
}
